import { useState } from 'react';
import { CheckCircle, Circle, Clock, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';

const ROADMAP = [
  {
    category: 'Phase 1 — Product Completion & Corporate Formation (Month 0–1)',
    color: '#22c55e',
    grad: ['#22c55e', '#06b6d4'],
    status: 'progress',
    items: [
      { title: 'Clickable Logo → Dashboard', desc: 'Car Collector Studio logo redirects to Dashboard/Home from anywhere in the app', status: 'done' },
      { title: 'Interactive Dashboard Widgets', desc: 'All dashboard tables and stat cards navigate directly to their module (Alerts, Vehicles, Expenses, Services, Insurance, Documents, etc.)', status: 'done' },
      { title: 'AI Valuation Engine — Integration & Testing', desc: 'Collector Score, Estimated Market Value and Comparables fully integrated and tested', status: 'done' },
      { title: 'Auto Market Data on Vehicle Add', desc: 'When a vehicle is added with full details, the system automatically scrapes relevant market sources via auto-fetch-comparables', status: 'done' },
      { title: 'Estimated Market Value Calculation', desc: 'Computed automatically from sold/asking comparables weighted by Collector Score', status: 'done' },
      { title: 'Historical Appreciation/Depreciation Trends', desc: 'Track value over time via valuation history and Portfolio charts', status: 'progress', note: 'Basic version live — deeper trend analysis planned' },
      { title: 'Valuation Insights & Comparable Market Data', desc: 'Market Intelligence reports + shared Comparables database per vehicle', status: 'done' },
      { title: 'Portfolio Onboarding — All Vehicles', desc: 'Onboard all vehicles owned by Theodoros into Car Collector Studio with full details', status: 'progress', note: '14 vehicles tracked so far' },
      { title: 'Document Upload & Organization', desc: 'Upload and organize ownership records, service history, insurance information and inspection reports per vehicle', status: 'progress', note: 'Documents module live — full backfill in progress' },
      { title: 'Incorporate Operating Company', desc: 'Incorporate a dedicated company to own and operate Car Collector Studio', status: 'planned' },
      { title: 'Transfer IP & Platform Rights', desc: 'Transfer all intellectual property, software assets and platform rights to the new company', status: 'planned' },
      { title: 'Establish Ownership Structure', desc: 'Geades GP — 70% · DTP — 30%', status: 'planned' },
    ],
  },
  {
    category: 'Phase 2 — Pilot Testing & Validation (Months 1–3)',
    color: '#4f8ef7',
    grad: ['#4f8ef7', '#7c3aed'],
    status: 'next',
    items: [
      { title: 'Onboard 2 Additional Users', desc: 'Bring on two additional users with their own vehicle collections', status: 'next', note: 'High priority' },
      { title: 'Real-World Feature Testing', desc: 'Test all major platform features under real-world conditions', status: 'planned' },
      { title: 'Gather User Feedback', desc: 'Collect feedback on usability, functionality, reporting and valuation accuracy', status: 'planned' },
      { title: 'Bug & Feature Tracking', desc: 'Identify bugs, feature requests and workflow improvements from pilot users', status: 'planned' },
      { title: 'Improve AI Valuation Accuracy', desc: 'Refine valuation model using additional vehicle data and market comparisons', status: 'planned' },
      { title: 'Validate Business Model', desc: 'Validate business model assumptions and user demand with real users', status: 'planned' },
      { title: 'Success Metric: 3 Active Portfolios', desc: 'Minimum of three active portfolios managed through the platform', status: 'next', note: 'Success metric for Phase 2' },
    ],
  },
  {
    category: 'Phase 3 — Product Refinement & Commercial Readiness (Months 3–5)',
    color: '#eab308',
    grad: ['#eab308', '#f97316'],
    status: 'planned',
    items: [
      { title: 'Professional Code Review', desc: 'Engage an experienced software developer to perform a comprehensive code review', status: 'planned' },
      { title: 'Database Optimization', desc: 'Optimize database structure, performance and scalability', status: 'planned' },
      { title: 'Resolve Technical Debt', desc: 'Resolve technical debt and eliminate known bugs', status: 'planned' },
      { title: 'UI/UX & Mobile Responsiveness', desc: 'Improve UI/UX and mobile responsiveness across the platform', status: 'progress', note: 'Mobile responsive layout already largely complete' },
      { title: 'Security & Backups', desc: 'Strengthen security, backup procedures and data integrity', status: 'planned' },
      { title: 'Branding, Pricing & Onboarding', desc: 'Finalize commercial branding, pricing strategy and customer onboarding process', status: 'planned' },
      { title: 'Prepare for Public Release', desc: 'Prepare the platform for public release and commercial sales', status: 'planned' },
      { title: 'Success Metric: Commercially Ready', desc: 'Commercially ready platform capable of onboarding paying customers', status: 'planned', note: 'Success metric for Phase 3' },
    ],
  },
  {
    category: 'Phase 4 — Investment & Growth Capital (Months 5–6)',
    color: '#a855f7',
    grad: ['#a855f7', '#ec4899'],
    status: 'planned',
    items: [
      { title: 'Present to Investors', desc: 'Present platform performance and pilot results to prospective investors', status: 'planned' },
      { title: 'Investor Materials & Projections', desc: 'Develop investor materials and financial projections', status: 'planned' },
      { title: 'Define Growth Capital Needs', desc: 'Establish growth capital requirements for scale-up', status: 'planned' },
      { title: 'Expand Shareholder Base', desc: 'Bring in strategic investors who contribute capital, expertise and industry connections', status: 'planned' },
      { title: 'Success Metric: Funding Round Closed', desc: 'Completion of a funding round to support expansion and growth initiatives', status: 'planned', note: 'Success metric for Phase 4' },
    ],
  },
  {
    category: 'Phase 5 — Scale & Market Expansion (Months 6–12)',
    color: '#06b6d4',
    grad: ['#06b6d4', '#4f8ef7'],
    status: 'planned',
    items: [
      { title: 'Customer Acquisition', desc: 'Expand customer acquisition efforts across target segments', status: 'planned' },
      { title: 'Onboard Collectors, Family Offices & Fleets', desc: 'Onboard additional vehicle collectors, enthusiasts, family offices and fleet operators', status: 'planned' },
      { title: 'Enhance AI Valuation & Market Intelligence', desc: 'Continue improving AI valuation capabilities and market intelligence features', status: 'planned' },
      { title: 'Advanced Analytics & Reporting', desc: 'Introduce advanced analytics and reporting tools', status: 'planned' },
      { title: 'Mobile Apps — iOS & Android', desc: 'Launch native mobile applications for iOS and Android', status: 'planned' },
      { title: 'Expand Market Data Integrations', desc: 'Expand integrations across additional auction houses and vehicle marketplaces', status: 'planned' },
      { title: 'Recurring Subscription Revenue', desc: 'Establish recurring subscription revenue as the core business model', status: 'planned' },
      { title: 'Scalable Infrastructure', desc: 'Build scalable operational and technical infrastructure to support future growth', status: 'planned' },
      { title: 'Success Metric: Revenue-Generating SaaS', desc: 'Transition from pilot-stage platform to a revenue-generating SaaS business with an expanding user base', status: 'planned', note: 'Success metric for Phase 5' },
    ],
  },
  {
    category: 'Long-Term Vision',
    color: '#c9a84c',
    grad: ['#c9a84c', '#e8c97a'],
    status: 'planned',
    items: [
      { title: 'Leading Vehicle Portfolio Management Platform', desc: 'Combine traditional fleet administration tools with AI-powered valuation, appreciation tracking, market intelligence and portfolio analytics', status: 'planned' },
      { title: 'Global Scalable Platform', desc: 'Create a scalable technology platform serving collectors, investors, family offices and fleet owners worldwide', status: 'planned' },
    ],
  },
];

const statusConfig = {
  done:     { label: 'Done',        color: '#22c55e', bg: 'rgba(34,197,94,0.1)',   border: 'rgba(34,197,94,0.25)'   },
  next:     { label: 'Next Up',     color: '#4f8ef7', bg: 'rgba(79,142,247,0.1)',  border: 'rgba(79,142,247,0.25)'  },
  progress: { label: 'In Progress', color: '#eab308', bg: 'rgba(234,179,8,0.1)',   border: 'rgba(234,179,8,0.25)'   },
  planned:  { label: 'Planned',     color: '#6b7280', bg: 'rgba(107,114,128,0.1)', border: 'rgba(107,114,128,0.2)'  },
};

export const Roadmap = () => {
  const [openSections, setOpenSections] = useState({ 0: true, 1: true, 2: true });
  const [filter, setFilter] = useState('all');

  const toggleSection = (i) => setOpenSections(s => ({ ...s, [i]: !s[i] }));

  const totalDone     = ROADMAP.flatMap(s => s.items).filter(i => i.status === 'done').length;
  const totalNext     = ROADMAP.flatMap(s => s.items).filter(i => i.status === 'next').length;
  const totalProgress = ROADMAP.flatMap(s => s.items).filter(i => i.status === 'progress').length;
  const totalPlanned  = ROADMAP.flatMap(s => s.items).filter(i => i.status === 'planned').length;
  const totalAll      = ROADMAP.flatMap(s => s.items).length;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Integration Roadmap</div>
          <div className="page-subtitle">Data sources, APIs, and features planned for Car Collector Studio</div>
        </div>
      </div>

      {/* Summary stats */}
      <div className="stats-grid" style={{ marginBottom: 24 }}>
        {[
          { label: 'Completed',   value: totalDone,     grad: ['#22c55e','#06b6d4'] },
          { label: 'Next Up',     value: totalNext,     grad: ['#4f8ef7','#7c3aed'] },
          { label: 'In Progress', value: totalProgress, grad: ['#eab308','#f97316'] },
          { label: 'Planned',     value: totalPlanned,  grad: ['#6b7280','#4b5563'] },
        ].map((item, i) => (
          <div key={i} className="stat-card" style={{ position: 'relative', overflow: 'hidden' }}>
            
            <div style={{ fontSize: 28, fontWeight: 900, letterSpacing: -0.5, background: `linear-gradient(135deg, ${item.grad[0]}, ${item.grad[1]})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{item.value}</div>
            <div className="stat-label" style={{ marginTop: 4 }}>{item.label}</div>
          </div>
        ))}
      </div>

      {/* Progress bar */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>Overall Progress</span>
          <span style={{ fontSize: 13, color: 'var(--text2)' }}>{totalDone} / {totalAll} items complete</span>
        </div>
        <div style={{ height: 8, background: 'var(--surface2)', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${(totalDone / totalAll) * 100}%`, background: 'linear-gradient(90deg, #22c55e, #06b6d4)', borderRadius: 4, transition: 'width 0.6s ease' }} />
        </div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 8 }}>
          {Math.round((totalDone / totalAll) * 100)}% complete · {totalNext + totalProgress} items actively in progress or next up
        </div>
      </div>

      {/* Filter pills */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { key: 'all',      label: 'All',          color: '#4f8ef7' },
          { key: 'done',     label: '✅ Done',       color: '#22c55e' },
          { key: 'next',     label: '🔵 Next Up',    color: '#4f8ef7' },
          { key: 'progress', label: '🟡 In Progress', color: '#eab308' },
          { key: 'planned',  label: '⚪ Planned',    color: '#6b7280' },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{ padding: '7px 16px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.15s', border: `1px solid ${filter === f.key ? f.color : 'var(--border)'}`, background: filter === f.key ? `${f.color}18` : 'var(--surface)', color: filter === f.key ? f.color : 'var(--text2)' }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Sections */}
      {ROADMAP.map((section, si) => {
        const filteredItems = filter === 'all' ? section.items : section.items.filter(i => i.status === filter);
        if (filteredItems.length === 0) return null;
        const isOpen = openSections[si] !== false;
        return (
          <div key={si} className="card" style={{ marginBottom: 16, padding: 0, overflow: 'hidden' }}>
            <div
              onClick={() => toggleSection(si)}
              style={{ padding: '16px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: `linear-gradient(135deg, ${section.grad[0]}0d, ${section.grad[1]}08)`, borderBottom: isOpen ? '1px solid var(--border)' : 'none' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: `linear-gradient(135deg, ${section.grad[0]}, ${section.grad[1]})`, boxShadow: `0 0 8px ${section.grad[0]}66` }} />
                <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>{section.category}</span>
                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: `${section.color}18`, color: section.color, border: `1px solid ${section.color}33`, fontFamily: 'DM Mono, monospace' }}>
                  {filteredItems.length} items
                </span>
              </div>
              {isOpen ? <ChevronUp size={16} style={{ color: 'var(--text3)' }} /> : <ChevronDown size={16} style={{ color: 'var(--text3)' }} />}
            </div>

            {isOpen && (
              <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {filteredItems.map((item, ii) => {
                  const sc = statusConfig[item.status] || statusConfig.planned;
                  return (
                    <div key={ii} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px', background: 'var(--surface2)', borderRadius: 10, border: '1px solid var(--border)' }}>
                      <div style={{ flexShrink: 0, marginTop: 2 }}>
                        {item.status === 'done' ? <CheckCircle size={16} style={{ color: '#22c55e' }} /> : item.status === 'next' ? <Circle size={16} style={{ color: '#4f8ef7' }} /> : item.status === 'progress' ? <Clock size={16} style={{ color: '#eab308' }} /> : <Circle size={16} style={{ color: '#6b7280', opacity: 0.4 }} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
                          <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>{item.title}</span>
                          <span style={{ fontSize: 10, padding: '1px 8px', borderRadius: 20, fontWeight: 600, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>{sc.label}</span>
                          {item.url && (
                            <a href={item.url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', opacity: 0.7 }} onClick={e => e.stopPropagation()}>
                              <ExternalLink size={11} />
                            </a>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5, marginBottom: item.note ? 4 : 0 }}>{item.desc}</div>
                        {item.note && <div style={{ fontSize: 11, color: 'var(--accent)', fontFamily: 'DM Mono, monospace' }}>→ {item.note}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};